import type {
  AppSurfaceId,
  UtilityType,
  WearableIdentityBaseline,
  WearableProfileBridgeSnapshot,
  WearableSignalType,
} from "./wearables.types";

function unique<T>(items: T[]): T[] {
  return Array.from(new Set(items));
}

function resolveModeBias(
  baseline: WearableIdentityBaseline,
): WearableProfileBridgeSnapshot["modeBias"] {
  if (baseline.preferredTrack) {
    return baseline.preferredTrack;
  }

  if (
    baseline.confirmedFamilies.includes("xr_headset") ||
    baseline.confirmedFamilies.includes("smart_glasses")
  ) {
    return "xr";
  }

  if (
    baseline.durableUtilities.includes("creator_session_preset") ||
    baseline.durableUtilities.includes("focus_mode_shift")
  ) {
    return "creator";
  }

  if (baseline.durableUtilities.includes("recovery_guidance")) {
    return "recovery";
  }

  if (
    baseline.durableUtilities.includes("adjust_daily_plan") ||
    baseline.durableUtilities.includes("workload_pacing")
  ) {
    return "readiness";
  }

  return "balanced";
}

function resolveRecommendedSurfaces(
  baseline: WearableIdentityBaseline,
  modeBias: WearableProfileBridgeSnapshot["modeBias"],
): AppSurfaceId[] {
  const surfaces = new Set<AppSurfaceId>(baseline.durableSurfaces);

  if (modeBias === "xr") {
    surfaces.add("xr_home");
    surfaces.add("creator_session_surface");
  }

  if (modeBias === "creator") {
    surfaces.add("creator_session_surface");
  }

  if (modeBias === "readiness") {
    surfaces.add("daily_readiness_surface");
    surfaces.add("readings_dashboard");
  }

  if (modeBias === "recovery") {
    surfaces.add("recovery_surface");
    surfaces.add("readings_dashboard");
  }

  return Array.from(surfaces);
}

function resolveUtilityBias(
  baseline: WearableIdentityBaseline,
  modeBias: WearableProfileBridgeSnapshot["modeBias"],
): UtilityType[] {
  const utilities = new Set<UtilityType>(baseline.durableUtilities);

  if (modeBias === "xr") {
    utilities.add("xr_mode_preset");
  }

  if (modeBias === "creator") {
    utilities.add("creator_session_preset");
  }

  if (modeBias === "readiness") {
    utilities.add("adjust_daily_plan");
  }

  if (modeBias === "recovery") {
    utilities.add("recovery_guidance");
  }

  return Array.from(utilities);
}

function resolveIdentitySignals(
  baseline: WearableIdentityBaseline,
): WearableSignalType[] {
  return unique(baseline.durableSignals).slice(0, 12);
}

function resolveXrDefaults(
  baseline: WearableIdentityBaseline,
  modeBias: WearableProfileBridgeSnapshot["modeBias"],
) {
  const recommended =
    modeBias === "xr" ||
    baseline.confirmedFamilies.includes("xr_headset") ||
    baseline.confirmedFamilies.includes("smart_glasses");

  const highlights: string[] = [];

  if (baseline.confirmedFamilies.includes("xr_headset")) {
    highlights.push("XR headset family present in baseline.");
  }

  if (baseline.confirmedFamilies.includes("smart_glasses")) {
    highlights.push("Smart-glasses family present in baseline.");
  }

  if (baseline.durableUtilities.includes("xr_mode_preset")) {
    highlights.push("XR mode preset is durably enabled.");
  }

  return {
    recommended,
    highlights:
      highlights.length > 0
        ? highlights
        : ["XR defaults remain available but not dominant."],
  };
}

function resolveCreatorDefaults(
  baseline: WearableIdentityBaseline,
  modeBias: WearableProfileBridgeSnapshot["modeBias"],
) {
  const recommended =
    modeBias === "creator" ||
    baseline.durableUtilities.includes("creator_session_preset") ||
    baseline.confirmedFamilies.includes("smart_glasses") ||
    baseline.confirmedFamilies.includes("xr_headset");

  const highlights: string[] = [];

  if (baseline.durableUtilities.includes("creator_session_preset")) {
    highlights.push("Creator session preset is durably enabled.");
  }

  if (baseline.confirmedFamilies.includes("smart_glasses")) {
    highlights.push("Smart-glasses profile can support creator-context continuity.");
  }

  if (baseline.confirmedFamilies.includes("xr_headset")) {
    highlights.push("XR family can support creator-mode continuity.");
  }

  return {
    recommended,
    highlights:
      highlights.length > 0
        ? highlights
        : ["Creator defaults remain available but not dominant."],
  };
}

function buildProfileSummary(
  baseline: WearableIdentityBaseline,
  modeBias: WearableProfileBridgeSnapshot["modeBias"],
): string[] {
  const summary: string[] = [];

  summary.push(`Wearable mode bias resolves to ${modeBias}.`);

  if (baseline.preferredTrack) {
    summary.push(`Preferred track persists as ${baseline.preferredTrack}.`);
  }

  if (baseline.confirmedDeviceCount > 0) {
    summary.push(
      `Confirmed wearable baseline includes ${baseline.confirmedDeviceCount} connected device${baseline.confirmedDeviceCount === 1 ? "" : "s"}.`,
    );
  }

  if (baseline.consentReviewCoverage !== "none") {
    summary.push(`Consent review coverage is ${baseline.consentReviewCoverage}.`);
  }

  if (baseline.durableUtilities.length > 0) {
    summary.push(
      `Durable utility bias includes ${baseline.durableUtilities.slice(0, 4).join(", ")}${baseline.durableUtilities.length > 4 ? ", ..." : ""}.`,
    );
  }

  return summary;
}

export function resolveWearableProfileBridgeSnapshot(
  baseline: WearableIdentityBaseline | null,
): WearableProfileBridgeSnapshot | null {
  if (!baseline || baseline.confirmedDeviceCount === 0) {
    return null;
  }

  const modeBias = resolveModeBias(baseline);

  return {
    version: "v1",
    generatedAt: new Date().toISOString(),
    preferredTrack: baseline.preferredTrack,
    modeBias,
    recommendedSurfaceIds: resolveRecommendedSurfaces(baseline, modeBias),
    utilityBias: resolveUtilityBias(baseline, modeBias),
    identitySignals: resolveIdentitySignals(baseline),
    xrDefaults: resolveXrDefaults(baseline, modeBias),
    creatorDefaults: resolveCreatorDefaults(baseline, modeBias),
    profileSummary: buildProfileSummary(baseline, modeBias),
  };
}
